import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { Edit20Regular } from "@fluentui/react-icons";
import { BIG_COPY } from "../copy";
import type { BigAnswers, BigStepKey } from "../types";
import { buildSections, damageTypeLabel } from "../serialize";
import { FONT_ACCENT, FONT_BODY } from "../../schadenmeldung/theme";
import { StepHead } from "../fields";

interface Props {
  answers: BigAnswers;
  jumpTo: (step: BigStepKey) => void;
  submitting: boolean;
  submitError: string | null;
}

const useStyles = makeStyles({
  section: {
    backgroundColor: "#fff",
    ...shorthands.border("1px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("12px"),
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    marginBottom: "12px",
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "10px",
  },
  sectionTitle: {
    margin: 0,
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_ACCENT,
    fontSize: "1.1rem",
    fontWeight: 500,
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "190px 1fr",
    gap: "10px",
    fontFamily: FONT_BODY,
    fontSize: "0.92rem",
    "@media (max-width: 560px)": {
      gridTemplateColumns: "1fr",
      gap: "2px",
    },
  },
  rowLabel: {
    color: "#6c6171",
    fontWeight: 600,
  },
  rowValue: {
    color: "var(--sm-primary-deep)",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  editBtn: {
    appearance: "none",
    backgroundColor: "transparent",
    color: "var(--sm-primary)",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: 600,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "9px",
    paddingRight: "9px",
    ...shorthands.border("1px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      ...shorthands.borderColor("var(--sm-primary)"),
      backgroundColor: "var(--sm-accent-soft)",
    },
  },
  error: {
    color: "#a63232",
    backgroundColor: "rgba(166,50,50,0.08)",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", "rgba(166,50,50,0.3)"),
    ...shorthands.borderRadius("8px"),
    fontFamily: FONT_BODY,
    fontSize: "0.9rem",
    marginBottom: "12px",
  },
  typeRow: {
    fontFamily: FONT_BODY,
    fontSize: "0.95rem",
    color: "var(--sm-primary-deep)",
    marginBottom: "14px",
  },
});

export default function SummaryStep({ answers, jumpTo, submitting, submitError }: Props) {
  const styles = useStyles();
  const sections = buildSections(answers).filter((s) => s.rows.length > 0);
  return (
    <div>
      <StepHead eyebrow={BIG_COPY.summary.eyebrow} title={BIG_COPY.summary.title} hint={BIG_COPY.summary.hint} />
      {submitError && <div className={styles.error}>{submitError}</div>}
      <div className={styles.typeRow}>
        <strong>Schadenart:</strong> {damageTypeLabel(answers.damageType)}
      </div>
      {sections.map((sec) => (
        <div key={sec.heading} className={styles.section}>
          <div className={styles.sectionHead}>
            <h3 className={styles.sectionTitle}>{sec.heading}</h3>
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => jumpTo(sec.target)}
              disabled={submitting}
            >
              <Edit20Regular style={{ fontSize: 15 }} />
              {BIG_COPY.nav.edit}
            </button>
          </div>
          <div className={styles.rows}>
            {sec.rows.map((r, i) => (
              <div key={i} className={styles.row}>
                <span className={styles.rowLabel}>{r.label}</span>
                <span className={styles.rowValue}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
